/* eslint-disable react-hooks/rules-of-hooks */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TableDataDefinition } from "../types";
import { FormattedNumber } from "../../FormattedNumber";
import { TicTacToeTableType } from "../../../graphql/generated";
import { SectionTableFragmentFragment } from "../../../graphql/SectionTableFragment.generated";
import { Column } from "react-table";

type TableType = Omit<SectionTableFragmentFragment, "metaData"> & {
  metaData: {
    gridSize: number;
    turnTime: number;
    bet: number;
    type: TicTacToeTableType;
    winsToComplete: number;
  };
};

type RowData = {
  players: number;
  gridSize: number;
  turnTime: number;
  bet: number;
  winsToComplete: number;
};

export const tableData: TableDataDefinition<RowData> = {
  useColumns: (isTournament: boolean) => {
    const { t } = useTranslation();
    return React.useMemo(() => {
      let result: Array<Column<RowData>> = [
        {
          Header: t<string>("players"),
          accessor: "players",
          Cell: ({ row }) => `${row.original.players} / 2`,
        },
        {
          Header: t<string>("tictactoe.grid_size"),
          accessor: "gridSize",
          Cell: ({ row }) =>
            `${row.original.gridSize} x ${row.original.gridSize}`,
        },
        {
          Header: t<string>("tictactoe.wins_to_complete_short"),
          accessor: "winsToComplete",
          Cell: ({ row }) => (
            <FormattedNumber value={row.original.winsToComplete} />
          ),
        },
        {
          Header: t<string>("turn_time"),
          accessor: "turnTime",
          Cell: ({ row }) => <FormattedNumber value={row.original.turnTime} />,
        },
      ];

      if (!isTournament)
        result = result.concat([
          {
            Header: t<string>("bet"),
            accessor: "bet",
            Cell: ({ row }) => <FormattedNumber value={row.original.bet} />,
          },
        ]);
      return result;
    }, [isTournament, t]);
  },
  makeRow: (table: TableType) => ({
    players: table.players.length,
    gridSize: table.metaData.gridSize,
    turnTime: table.metaData.turnTime,
    bet: table.metaData.bet,
    winsToComplete: table.metaData.winsToComplete,
  }),

  calculateEntryFee: (table) => {
    return table.metaData.type === TicTacToeTableType.Private
      ? null
      : table.metaData.bet;
  },

  getRequiredPlayers: () => 2,

  getDetails: (table: TableType) => {
    const { t } = useTranslation();
    return (
      <>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("players")}</div>
          <div className="px-2">{`${table.players.length} / 2`}</div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("tictactoe.grid_size")}</div>
          <div className="px-2">{`${table.metaData.gridSize} x ${table.metaData.gridSize}`}</div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("tictactoe.wins_to_complete_short")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.winsToComplete} />
          </div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("turn_time")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.turnTime} />
          </div>
        </div>
        <div className="flex -mx-2 justify-between">
          <div className="px-2">{t("bet")}</div>
          <div className="px-2">
            <FormattedNumber value={table.metaData.bet} />
          </div>
        </div>
      </>
    );
  },
};
